/**
 * Session scheduling: one-off sessions and weekly rules ("every Wed at 8:00 PM", every N weeks, optional end date),
 * with per-occurrence skips and moves. Rules are stored as LOCAL wall-clock times in the campaign's IANA time zone,
 * so "8:00 PM" stays 8:00 PM across daylight-saving changes. Pure functions (no libraries; uses Intl).
 */
export const DEFAULT_TIMEZONE = "America/New_York";
export const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_MS = 86_400_000;
export const isValidTimezone = (tz) => {
    if (typeof tz !== "string" || !tz || tz.length > 64)
        return false;
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
};
export const isDateStr = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) && new Date(`${s}T00:00:00Z`).toISOString().startsWith(s);
export const isTimeStr = (s) => typeof s === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
const fmtCache = new Map();
const partsFmt = (tz) => {
    let f = fmtCache.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
        fmtCache.set(tz, f);
    }
    return f;
};
/** Wall-clock fields of an instant in a zone. */
export function zonedParts(ms, tz) {
    const p = {};
    for (const x of partsFmt(tz).formatToParts(new Date(ms)))
        if (x.type !== "literal")
            p[x.type] = Number(x.value);
    const y = p.year, m = p.month, d = p.day;
    return { y, m, d, hh: p.hour % 24, mm: p.minute, ss: p.second, weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
}
/** Offset of the zone from UTC at an instant, in ms (e.g. -4 h for New York in summer). */
export function tzOffsetMs(ms, tz) {
    const p = zonedParts(ms, tz);
    return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss) - Math.floor(ms / 1000) * 1000;
}
/**
 * The instant a local wall-clock time happens in a zone. Times that don't exist (the hour skipped when clocks go
 * forward) move forward by the gap (2:30 AM → 3:30 AM); repeated times (clocks go back) use the first one.
 */
export function zonedToUtc(date, time, tz) {
    const [y, m, d] = date.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    const guess = Date.UTC(y, m - 1, d, hh, mm);
    const o1 = tzOffsetMs(guess, tz);
    const t1 = guess - o1;
    const o2 = tzOffsetMs(t1, tz);
    if (o1 === o2)
        return t1;
    const t2 = guess - o2;
    // A candidate is right when its own offset maps it back to the wall-clock time asked for.
    const ok = [t1, t2].filter((t) => t + tzOffsetMs(t, tz) === guess);
    return ok.length ? Math.min(...ok) : Math.max(t1, t2);
}
/** Local date (YYYY-MM-DD) of an instant in a zone. */
export function localDate(ms, tz) {
    const p = zonedParts(ms, tz);
    return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}
const dayNum = (date) => Date.parse(`${date}T00:00:00Z`) / DAY_MS;
const dateOf = (n) => new Date(n * DAY_MS).toISOString().slice(0, 10);
/** Original (un-moved) occurrences of a rule whose local date is in [fromDate, toDate]. */
function baseDates(rule, fromDate, toDate) {
    if (rule.kind === "once")
        return rule.startDate >= fromDate && rule.startDate <= toDate ? [rule.startDate] : [];
    const start = dayNum(rule.startDate);
    const first = Math.max(start, dayNum(fromDate));
    const last = Math.min(rule.endDate ? dayNum(rule.endDate) : Infinity, dayNum(toDate));
    const interval = Math.max(1, Math.floor(rule.intervalWeeks || 1));
    // Weeks are counted from the Sunday on or before the start date.
    const anchor = start - new Date(start * DAY_MS).getUTCDay();
    const days = new Set(rule.weekdays);
    const out = [];
    for (let n = first; n <= last; n++) {
        const wd = new Date(n * DAY_MS).getUTCDay();
        if (!days.has(wd))
            continue;
        if (Math.floor((n - anchor) / 7) % interval !== 0)
            continue;
        out.push(dateOf(n));
        if (out.length > 1000)
            break;
    }
    return out;
}
/**
 * Occurrences of the rules starting in [fromMs, toMs), soonest first, with skips (cancelled: true) and moves applied.
 * includeCancelled = false drops skipped ones.
 */
export function expandSchedule(rules, exceptions, tz, fromMs, toMs, includeCancelled = false) {
    // Moves can pull an occurrence in from up to 60 days away.
    const fromDate = localDate(fromMs - 61 * DAY_MS, tz);
    const toDate = localDate(toMs + 61 * DAY_MS, tz);
    const ex = new Map(exceptions.map((e) => [`${e.ruleId}|${e.date}`, e]));
    const out = [];
    for (const r of rules) {
        for (const date of baseDates(r, fromDate, toDate)) {
            const e = ex.get(`${r.id}|${date}`);
            const moved = e?.action === "move" && !!e.newAt;
            const at = moved ? Date.parse(e.newAt) : zonedToUtc(date, r.time, tz);
            const cancelled = e?.action === "skip";
            if (at < fromMs || at >= toMs || (cancelled && !includeCancelled))
                continue;
            out.push({ ruleId: r.id, date, at: new Date(at).toISOString(), durationMin: r.durationMin, note: r.note, recurring: r.kind === "weekly", moved, cancelled });
        }
    }
    return out.sort((a, b) => a.at.localeCompare(b.at) || a.ruleId - b.ruleId);
}
/** The next (not cancelled) occurrence that hasn't ended yet, or null. */
export function nextOccurrence(rules, exceptions, tz, nowMs, horizonDays = 400) {
    const list = expandSchedule(rules, exceptions, tz, nowMs - 6 * 3600_000, nowMs + horizonDays * DAY_MS);
    return list.find((o) => Date.parse(o.at) + o.durationMin * 60_000 > nowMs) ?? null;
}
const fmtTime = (time) => {
    const [h, m] = time.split(":").map(Number);
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
/** "Every Wednesday at 8:00 PM", "Every other week on Mon and Thu at 7:30 PM until 2027-01-31", "Once on 2026-10-28 at 8:00 PM". */
export function describeRule(r) {
    if (r.kind === "once")
        return `Once on ${r.startDate} at ${fmtTime(r.time)}`;
    const days = [...r.weekdays].sort((a, b) => a - b);
    const names = days.length === 1 ? WEEKDAY_NAMES[days[0]] : days.map((d) => WEEKDAY_NAMES[d].slice(0, 3)).join(" and ").replace(/ and (?=.* and )/g, ", ");
    const every = r.intervalWeeks <= 1 ? (days.length === 1 ? `Every ${names}` : `Every week on ${names}`) : r.intervalWeeks === 2 ? `Every other week on ${names}` : `Every ${r.intervalWeeks} weeks on ${names}`;
    return `${every} at ${fmtTime(r.time)}${r.startDate ? ` from ${r.startDate}` : ""}${r.endDate ? ` until ${r.endDate}` : ""}`;
}
/** Problems with a rule input (empty = fine). */
export function ruleProblems(i) {
    const p = [];
    if (i.kind !== "once" && i.kind !== "weekly")
        p.push("Pick one-off or repeating");
    if (!isDateStr(i.startDate))
        p.push("Pick a date");
    if (!isTimeStr(i.time))
        p.push("Pick a time (HH:MM)");
    if (i.kind === "weekly") {
        if (!Array.isArray(i.weekdays) || !i.weekdays.length || i.weekdays.some((d) => !Number.isInteger(d) || d < 0 || d > 6))
            p.push("Pick at least one day of the week");
        if (i.intervalWeeks !== undefined && !(Number.isInteger(i.intervalWeeks) && i.intervalWeeks >= 1 && i.intervalWeeks <= 8))
            p.push("Repeat every 1 to 8 weeks");
        if (i.endDate != null && (!isDateStr(i.endDate) || (isDateStr(i.startDate) && i.endDate < i.startDate)))
            p.push("The end date must be after the start");
    }
    if (i.durationMin !== undefined && !(Number.isInteger(i.durationMin) && i.durationMin >= 15 && i.durationMin <= 720))
        p.push("Length must be 15 minutes to 12 hours");
    return p;
}
// ---------------------------------------------------------------- iCalendar
const icsEscape = (s) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
const icsDate = (ms) => new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
/** Fold lines to 75 octets (RFC 5545), roughly by characters. */
const fold = (line) => line.match(/.{1,73}/gu)?.join("\r\n ") ?? line;
export function icsCalendar(name, events, nowMs) {
    const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//AI Dungeon Master//Schedule//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH", `X-WR-CALNAME:${icsEscape(name)}`];
    for (const e of events) {
        lines.push("BEGIN:VEVENT", `UID:${e.uid}`, `DTSTAMP:${icsDate(nowMs)}`, `DTSTART:${icsDate(e.startMs)}`, `DTEND:${icsDate(e.startMs + e.durationMin * 60_000)}`, `SUMMARY:${icsEscape(e.summary)}`);
        if (e.description)
            lines.push(`DESCRIPTION:${icsEscape(e.description)}`);
        if (e.url)
            lines.push(`URL:${e.url}`);
        if (e.cancelled)
            lines.push("STATUS:CANCELLED");
        lines.push("END:VEVENT");
    }
    lines.push("END:VCALENDAR");
    return lines.map(fold).join("\r\n") + "\r\n";
}
//# sourceMappingURL=schedule.js.map